import { supabase } from './supabase'

export interface Country {
  id: string
  code: string
  name: string
  flag_emoji: string
  currency_code: string
  currency_symbol: string
}

export interface ExchangeRate {
  id: string
  from_country_id: string
  to_country_id: string
  rate: number
  is_active: boolean
}

export interface PaymentMethod {
  id: string
  name: string
  description: string | null
  icon: string | null
  is_active: boolean
}

export interface TransferReason {
  id: string
  name: string
  description: string | null
  is_active: boolean
}

export interface Beneficiary {
  id: string
  user_id: string
  name: string
  document_type: string
  document_number: string
  beneficiary_type: string
  country_id: string
  bank_name: string
  account_type: string
  account_number: string
  created_at: string
  updated_at: string
  country?: Country
}

export interface UserBankAccount {
  id: string
  user_id: string
  account_name: string
  bank_name: string
  account_type: string
  account_number: string
  routing_number?: string
  country_id: string
  is_primary: boolean
  is_active: boolean
  created_at: string
  updated_at: string
  country?: Country
}

export interface PaymentPartner {
  id: string
  country_id: string
  partner_name: string
  document_type: string
  document_number: string
  bank_name: string
  account_type: string
  account_number: string
  is_active: boolean
  created_at: string
  updated_at: string
  country?: Country
}

export interface Transfer {
  id: string
  user_id: string
  beneficiary_id: string
  bank_account_id: string
  transfer_reason_id: string
  from_amount: number
  from_currency: string
  to_amount: number
  to_currency: string
  exchange_rate: number
  associated_cost: number
  total_amount: number
  concept?: string
  digital_usd_confirmation: boolean
  operation_type: string
  status: string
  expires_at: string
  created_at: string
  updated_at: string
  beneficiary?: Beneficiary
  bank_account?: UserBankAccount
  transfer_reason?: TransferReason
}

// Get all countries
export async function getCountries(): Promise<Country[]> {
  const { data, error } = await supabase
    .from('countries')
    .select('*')
    .order('name')

  if (error) {
    console.error('Error fetching countries:', error)
    throw error
  }

  return data || []
}

// Get exchange rate between two countries
export async function getExchangeRate(fromCountryCode: string, toCountryCode: string): Promise<number | null> {
  try {
    // First get the country IDs
    const { data: fromCountry, error: fromError } = await supabase
      .from('countries')
      .select('id')
      .eq('code', fromCountryCode)
      .single()

    if (fromError) {
      console.error('Error fetching from country:', fromError)
      return null
    }

    const { data: toCountry, error: toError } = await supabase
      .from('countries')
      .select('id')
      .eq('code', toCountryCode)
      .single()

    if (toError) {
      console.error('Error fetching to country:', toError)
      return null
    }

    // Then get the exchange rate
    const { data, error } = await supabase
      .from('exchange_rates')
      .select('rate')
      .eq('from_country_id', fromCountry.id)
      .eq('to_country_id', toCountry.id)
      .eq('is_active', true)
      .single()

    if (error) {
      console.error('Error fetching exchange rate:', error)
      return null
    }

    return data?.rate || null
  } catch (error) {
    console.error('Error in getExchangeRate:', error)
    return null
  }
}

// Get all payment methods
export async function getPaymentMethods(): Promise<PaymentMethod[]> {
  const { data, error } = await supabase
    .from('payment_methods')
    .select('*')
    .eq('is_active', true)
    .order('name')

  if (error) {
    console.error('Error fetching payment methods:', error)
    throw error
  }

  return data || []
}

// Get all transfer reasons
export async function getTransferReasons(): Promise<TransferReason[]> {
  const { data, error } = await supabase
    .from('transfer_reasons')
    .select('*')
    .eq('is_active', true)
    .order('name')

  if (error) {
    console.error('Error fetching transfer reasons:', error)
    throw error
  }

  return data || []
}

// Calculate converted amount
export function calculateConvertedAmount(amount: number, rate: number): number {
  return Math.round(amount * rate * 100) / 100 // Round to 2 decimal places
}

// Get beneficiaries for a user
export async function getBeneficiaries(userId: string): Promise<Beneficiary[]> {
  const { data, error } = await supabase
    .from('beneficiaries')
    .select(`
      *,
      country:countries(*)
    `)
    .eq('user_id', userId)
    .order('created_at', { ascending: false })

  if (error) {
    console.error('Error fetching beneficiaries:', error)
    throw error
  }

  return data || []
}

// Create a new beneficiary
export async function createBeneficiary(beneficiary: Omit<Beneficiary, 'id' | 'created_at' | 'updated_at' | 'country'>): Promise<Beneficiary> {
  const { data, error } = await supabase
    .from('beneficiaries')
    .insert([beneficiary])
    .select(`
      *,
      country:countries(*)
    `)
    .single()

  if (error) {
    console.error('Error creating beneficiary:', error)
    throw error
  }

  return data
}

// Update a beneficiary
export async function updateBeneficiary(id: string, updates: Partial<Beneficiary>): Promise<Beneficiary> {
  const { data, error } = await supabase
    .from('beneficiaries')
    .update(updates)
    .eq('id', id)
    .select(`
      *,
      country:countries(*)
    `)
    .single()

  if (error) {
    console.error('Error updating beneficiary:', error)
    throw error
  }

  return data
}

// Delete a beneficiary
export async function deleteBeneficiary(id: string): Promise<void> {
  const { error } = await supabase
    .from('beneficiaries')
    .delete()
    .eq('id', id)

  if (error) {
    console.error('Error deleting beneficiary:', error)
    throw error
  }
}

// Search beneficiaries
export async function searchBeneficiaries(userId: string, searchTerm: string): Promise<Beneficiary[]> {
  const { data, error } = await supabase
    .from('beneficiaries')
    .select(`
      *,
      country:countries(*)
    `)
    .eq('user_id', userId)
    .or(`name.ilike.%${searchTerm}%,document_number.ilike.%${searchTerm}%`)
    .order('created_at', { ascending: false })

  if (error) {
    console.error('Error searching beneficiaries:', error)
    throw error
  }

  return data || []
}

// Get user bank accounts
export async function getUserBankAccounts(userId: string): Promise<UserBankAccount[]> {
  const { data, error } = await supabase
    .from('user_bank_accounts')
    .select(`
      *,
      country:countries(*)
    `)
    .eq('user_id', userId)
    .eq('is_active', true)
    .order('is_primary', { ascending: false })
    .order('created_at', { ascending: false })

  if (error) {
    console.error('Error fetching user bank accounts:', error)
    throw error
  }

  return data || []
}

// Create a new user bank account
export async function createUserBankAccount(account: Omit<UserBankAccount, 'id' | 'created_at' | 'updated_at' | 'country'>): Promise<UserBankAccount> {
  const { data, error } = await supabase
    .from('user_bank_accounts')
    .insert([account])
    .select(`
      *,
      country:countries(*)
    `)
    .single()

  if (error) {
    console.error('Error creating user bank account:', error)
    throw error
  }

  return data
}

// Update a user bank account
export async function updateUserBankAccount(id: string, updates: Partial<UserBankAccount>): Promise<UserBankAccount> {
  const { data, error } = await supabase
    .from('user_bank_accounts')
    .update(updates)
    .eq('id', id)
    .select(`
      *,
      country:countries(*)
    `)
    .single()

  if (error) {
    console.error('Error updating user bank account:', error)
    throw error
  }

  return data
}

// Delete a user bank account
export async function deleteUserBankAccount(id: string): Promise<void> {
  const { error } = await supabase
    .from('user_bank_accounts')
    .delete()
    .eq('id', id)

  if (error) {
    console.error('Error deleting user bank account:', error)
    throw error
  }
}

// Set primary bank account
export async function setPrimaryBankAccount(userId: string, accountId: string): Promise<void> {
  // First, unset all primary accounts
  await supabase
    .from('user_bank_accounts')
    .update({ is_primary: false })
    .eq('user_id', userId)

  // Then set the selected account as primary
  const { error } = await supabase
    .from('user_bank_accounts')
    .update({ is_primary: true })
    .eq('id', accountId)
    .eq('user_id', userId)

  if (error) {
    console.error('Error setting primary bank account:', error)
    throw error
  }
}

// Create a new transfer
export async function createTransfer(transfer: Omit<Transfer, 'id' | 'created_at' | 'updated_at' | 'beneficiary' | 'bank_account' | 'transfer_reason'>): Promise<Transfer> {
  const { data, error } = await supabase
    .from('transfers')
    .insert([transfer])
    .select(`
      *,
      beneficiary:beneficiaries(*),
      bank_account:user_bank_accounts(*),
      transfer_reason:transfer_reasons(*)
    `)
    .single()

  if (error) {
    console.error('Error creating transfer:', error)
    throw error
  }

  return data
}

// Get transfer by ID
export async function getTransfer(transferId: string): Promise<Transfer | null> {
  const { data, error } = await supabase
    .from('transfers')
    .select(`
      *,
      beneficiary:beneficiaries(*),
      bank_account:user_bank_accounts(*),
      transfer_reason:transfer_reasons(*)
    `)
    .eq('id', transferId)
    .single()

  if (error) {
    console.error('Error fetching transfer:', error)
    throw error
  }

  return data
}

// Update transfer status
export async function updateTransferStatus(transferId: string, status: string): Promise<Transfer> {
  const { data, error } = await supabase
    .from('transfers')
    .update({ status })
    .eq('id', transferId)
    .select(`
      *,
      beneficiary:beneficiaries(*),
      bank_account:user_bank_accounts(*),
      transfer_reason:transfer_reasons(*)
    `)
    .single()

  if (error) {
    console.error('Error updating transfer status:', error)
    throw error
  }

  return data
}

// Get user transfers
export async function getUserTransfers(userId: string): Promise<Transfer[]> {
  const { data, error } = await supabase
    .from('transfers')
    .select(`
      *,
      beneficiary:beneficiaries(*),
      bank_account:user_bank_accounts(*),
      transfer_reason:transfer_reasons(*)
    `)
    .eq('user_id', userId)
    .order('created_at', { ascending: false })

  if (error) {
    console.error('Error fetching user transfers:', error)
    throw error
  }

  return data || []
}

// Get recent user transfers (last 7)
export async function getRecentUserTransfers(userId: string): Promise<Transfer[]> {
  const { data, error } = await supabase
    .from('transfers')
    .select(`
      *,
      beneficiary:beneficiaries(*),
      bank_account:user_bank_accounts(*),
      transfer_reason:transfer_reasons(*)
    `)
    .eq('user_id', userId)
    .order('created_at', { ascending: false })
    .limit(6)

  if (error) {
    console.error('Error fetching recent user transfers:', error)
    throw error
  }

  return data || []
}

// Get payment partner by country
export async function getPaymentPartnerByCountry(countryCode: string): Promise<PaymentPartner | null> {
  const { data, error } = await supabase
    .from('payment_partners')
    .select(`
      *,
      country:countries(*)
    `)
    .eq('is_active', true)
    .eq('country.code', countryCode)
    .limit(1)

  if (error) {
    console.error('Error fetching payment partner:', error)
    throw error
  }

  // Return the first payment partner found, or null if none
  return data && data.length > 0 ? data[0] : null
}

// Get payment partner by country ID
export async function getPaymentPartnerByCountryId(countryId: string): Promise<PaymentPartner | null> {
  const { data, error } = await supabase
    .from('payment_partners')
    .select(`
      *,
      country:countries(*)
    `)
    .eq('is_active', true)
    .eq('country_id', countryId)
    .limit(1)

  if (error) {
    console.error('Error fetching payment partner:', error)
    throw error
  }

  // Return the first payment partner found, or null if none
  return data && data.length > 0 ? data[0] : null
}
